import { format, isValid } from 'date-fns';
import { AlertTriangleIcon, EyeIcon } from 'lucide-react';
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { Checkbox } from 'src/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'src/components/ui/table';
import { TooltipProvider } from 'src/components/ui/tooltip';
import ApproveBulk from 'src/components/workflow/bulk/ApproveBulk';
import DownloadBulk from 'src/components/workflow/bulk/DownloadBulk';
import ForwardBulk from 'src/components/workflow/bulk/ForwardBulk';
import RejectBulk from 'src/components/workflow/bulk/RejectBulk';
import { YYYYMMDDtoDate } from 'src/functions/format';
import { getWindevLang } from 'src/lib/lang';
import lodash from 'lodash'
import { useQuery } from 'react-query';
import axios from 'src/axios/axios';
import { Sheet, SheetContent, SheetTrigger } from 'src/components/ui/sheet';
import Preview from 'src/components/files/Preview';

type P = {
    activeDocument:any;
    docid_added_wf:any;
    data:any;
    filteredDocuments:any;
    active_index:any;
    communication:any;
    refetchCommunication:any;
    handleDownloadFile:any;
    setactive_index:any;
    doctypes:any;
    metatypes:any;
    getMetatypeOMS:any;
    setactive_approve:any;
    setactive_forward:any;
    setactive_reject:any;

    onSuccess?: () => void
    handleOnReject?: any
    handleOnApprove?: any
    handleOnForward?: any
}

const PreviewDocumentTable = ({docid, filename}:{docid:number, filename:string}) => {
    const [open, setopen] = useState(false);

    useEffect(()=>{
        if(!open){ return }
        console.log(docid)
    }, [open])

    return (
        <Sheet open={open} onOpenChange={(o)=>{setopen(o)}}>
            <SheetTrigger asChild><button className='flex items-center justify-center w-8 h-8 rounded-full min-w-8 min-h-8 hover:bg-slate-200'><EyeIcon className='w-4 h-4 text-slate-600'/></button></SheetTrigger>
            <SheetContent position='bottom' className='max-h-[90dvh] overflow-auto min-h-[400px] h-full'>
                <div>
                    <Preview docid={docid} filename={filename} />
                </div>
            </SheetContent>
        </Sheet>
    )
}

const DataRow = ({document, data, getColumns, selected_docids, toggleSelect, doctypes}:{document:any, data:any, getColumns:any, selected_docids:any, toggleSelect:any, doctypes:any}) => {
    const { i18n } = useTranslation()

    return (
        <TableRow key={`row-${document.docid}`} className={`${!document?.options?.wf && 'opacity-50'}`}>
            <TableCell>{document?.options?.wf ? <div className='flex items-center'><Checkbox onCheckedChange={(c)=>{toggleSelect(document?.docid)}} checked={selected_docids?.includes(document.docid) || false}/></div> : <></>}</TableCell>
            <TableCell>{document?.docid}</TableCell>
            <TableCell>{Array.isArray(doctypes) ? (doctypes?.find((i:any)=>i.doctype===document.doctype)?.docoms?.find((i:any)=>i.nationid===getWindevLang(i18n.language))?.oms) : document.doctype}</TableCell>
            <TableCell>{document?.docdate ? (isValid(YYYYMMDDtoDate(document.docdate)) ? format(YYYYMMDDtoDate(document.docdate), "dd/MM/yyyy") : '') : ''}</TableCell>
            <TableCell>{document?.filename}</TableCell>
            {data && data.documents && Array.isArray(getColumns(data.documents)) && getColumns(data.documents).map((v:any, i:any)=>(
                <TableCell key={i}>{document?.metadata?.find((md:any) => md.metatype === v)?.metavalue || ''}</TableCell>
            ))}
            <TableCell><div className='flex items-center justify-end'><PreviewDocumentTable filename={document?.filename || ''} docid={document?.docid || 0}/></div></TableCell>
        </TableRow>
    )
}

const TableView = ({docid_added_wf, data, doctypes, metatypes, onSuccess, handleOnReject, handleOnApprove, handleOnForward, filteredDocuments}:P) => {
    const { t, i18n } = useTranslation()

    const { data:allVerificationSettings } = useQuery({
        queryKey: [`all-verification-settings`],
        queryFn: async () => {
            try {
                const arrWfActionId = lodash.uniqWith(data?.documents?.map((d:any)=>d?.wf?.wfdef_actionid).filter((i:any)=>typeof(i)==='number'), lodash.isEqual)
                
                const promises = arrWfActionId.map(actionid => 
                    axios.get(`wf/verificationsettings?token=${data?.token}&wfdef_actionid=${actionid}`)
                    .then(({data}) => {
                        if(data.errorcode!==0){throw new Error(`getting wf vs ${actionid} failed`)}
                        return {actionid:actionid, ...data.object}
                    })
                );

                const results = await Promise.all(promises);

                return results

            } catch (error) {
                return null
            }
        },
        refetchOnWindowFocus: false,
        enabled: !!(data?.documents)
    })

    const getColumns = (results:DocumentObject[]) => {
        try { 
            if(!results){ return []; }
            if(!metatypes){ return []; }
            let result:any[] = []
            let metatypeArray:string[] = []
            results.forEach(element => {
                return element.metadata.forEach(md => {
                    if(!metatypeArray.includes(md.metatype)){ metatypeArray.push(md.metatype) }
                })
            });
            result = metatypeArray.filter(mt => {
                const item = metatypes?.find((i:any)=>i.metatype===mt)
                return item?.columndisplay
            })
            return result
        } catch (e) { return [] }
    }

    const [selected_docids, setselected_docids] = useState<number[]>([]);

    function toggleSelect(id:number) {
        setselected_docids(p=>{
            let u = [...p]
            const i = u.indexOf(id)
            if(i<0){ u.push(id) }
            else{ u.splice(i, 1) }
            return u
        })
    }
    
    function toggleSelectAll() {
        setselected_docids(p=>{
            let u = [...p]
            if(p.length > 0){ u = [] }
            else {
                if(Array.isArray(data.documents)){
                    const d = data.documents.filter((d:any)=>d.options?.wf).map((d:any)=>d.docid)
                    u = d
                }
            }
            return u
        })
    }

    const allEqual = (arr:any[]) => arr.every( v => v === arr[0] )

    function allSelectedSameVerificationSettings() {
        let isSame = false;
        if(!Array.isArray(allVerificationSettings)){ return isSame; }
        if(!data){ return isSame; }
        if(!Array.isArray(data.documents)){ return isSame; }

        const selectedActionIds:number[] = []
        selected_docids.forEach(docid => {
            const wfid = data?.documents?.find((d:any)=>d.docid===docid)?.wf?.wfdef_actionid || 0
            selectedActionIds.push(wfid)
        });
        
        isSame = allEqual(selectedActionIds)

        return isSame
    }

    const token = data?.token || ''
    const actionid = data?.documents?.find((d:any)=>d.docid === selected_docids[0])?.wf?.wfdef_actionid || 0

    return (
    <>
    <div className='container flex-1 py-10 overflow-auto'>

        {(docid_added_wf.length === data?.documents?.length) && 
        <div className='flex flex-col items-center justify-center w-full gap-10'>
            <p className='text-4xl font-bold lg:text-7xl'>{t('Bedankt')}.</p>
            <p>{t('Alle documenten zijn behandeld')}</p>
        </div>
        }

        {!(docid_added_wf.length === (data?.documents && data?.documents?.length)) && <>
        <div className='sticky flex items-center justify-between w-full p-1 bg-white border rounded-md shadow-sm -top-8 min-h-10'>
            <TooltipProvider delayDuration={0}>
            <div className='flex items-center gap-1'>
                <ApproveBulk onSuccess={()=>{handleOnApprove([...selected_docids]); setselected_docids([]); }} metatypes={metatypes} disabled={!allSelectedSameVerificationSettings()} docids={selected_docids} verification_settings={allVerificationSettings?.find(s=>s.actionid===actionid) || undefined}/>
                <RejectBulk onSuccess={()=>{handleOnApprove([...selected_docids]); setselected_docids([]); }} metatypes={metatypes} disabled={!allSelectedSameVerificationSettings()} docids={selected_docids} verification_settings={allVerificationSettings?.find(s=>s.actionid===actionid) || undefined}/>
                {selected_docids?.length === 1 && <ForwardBulk onSuccess={()=>{handleOnApprove([...selected_docids]); setselected_docids([]); }} docids={selected_docids} />}
            </div>
            <div>
                
            </div>
            </TooltipProvider>
        </div>

        {(selected_docids.length > 0 && data && filteredDocuments && !allSelectedSameVerificationSettings()) && <div className='fixed z-20 flex items-center gap-2 p-4 -translate-x-1/2 -translate-y-1/2 border border-red-100 rounded-sm shadow-lg bg-red-50 top-1/2 left-1/2'>
            <AlertTriangleIcon className='w-4 h-4 text-red-600'/>
            <p className='font-medium text-red-600 whitespace-nowrap'>Niet alle geselecteerde documenten hebben dezelfde <span className='underline'>"Workflow ID"</span></p>
        </div>}

        <div className='flex flex-col gap-4 mt-10'>
            <div className='overflow-auto border rounded-md'>
                <Table>
                    <TableHeader className='bg-slate-100'><TableRow>
                        <TableHead className='whitespace-nowrap'><div className='flex items-center'><Checkbox checked={selected_docids?.length > 0 || false} onCheckedChange={()=>{toggleSelectAll()}}/></div></TableHead>
                        <TableHead className='whitespace-nowrap'>Document ID</TableHead>
                        <TableHead className='whitespace-nowrap'>Document type</TableHead>
                        <TableHead className='whitespace-nowrap'>Document datum</TableHead>
                        <TableHead className='whitespace-nowrap'>Bestandsnaam</TableHead>
                        {data && filteredDocuments && Array.isArray(getColumns(filteredDocuments)) && getColumns(filteredDocuments).map((v, i)=>(
                            <TableHead key={i}>{Array.isArray(metatypes) ? metatypes?.find((i:any)=>i.metatype===v)?.metatype_oms?.find((i:any)=>i.nationid===getWindevLang(i18n.language))?.oms || v : ''}</TableHead>
                        ))}
                        <TableHead></TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                        {(data && Array.isArray(filteredDocuments)) && filteredDocuments.map((document:any)=>(
                            <DataRow doctypes={doctypes} selected_docids={selected_docids} toggleSelect={toggleSelect} data={data} document={document} getColumns={getColumns} key={`row-${document.docid}`}/>
                        ))}
                    </TableBody>
                </Table>
            </div>
        </div>
        </>}
    </div>
    </>
  )
}

export default TableView